import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PaperPlaneTilt, ArrowLeft, ShieldWarning } from "@phosphor-icons/react";

export default function ChatPage({ scope = "rental" }) {
  const params = useParams();
  const targetId = params.rentalId || params.bookingId;
  const endpoint = `/chat/${scope}/${targetId}`;
  const { user } = useAuth();
  const [thread, setThread] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);
  const lastCreatedRef = useRef(null);

  const poll = async () => {
    try {
      const url = lastCreatedRef.current
        ? `${endpoint}?since=${encodeURIComponent(lastCreatedRef.current)}`
        : endpoint;
      const { data } = await api.get(url);
      if (lastCreatedRef.current) {
        if (data.messages?.length) {
          setThread((prev) => ({ ...(prev || data), messages: [...(prev?.messages || []), ...data.messages] }));
          lastCreatedRef.current = data.messages[data.messages.length - 1].created_at;
        }
      } else {
        setThread(data);
        if (data.messages?.length) lastCreatedRef.current = data.messages[data.messages.length - 1].created_at;
      }
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    poll();
    const t = setInterval(poll, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [thread?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      const { data } = await api.post(endpoint, { body: text });
      setThread((prev) => prev ? { ...prev, messages: [...prev.messages, data] } : prev);
      lastCreatedRef.current = data.created_at;
      setBody("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  if (!thread) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const myRole = scope === "booking"
    ? (thread.hotel_owner_id === user?.id ? "hotel" : "guest")
    : (thread.owner_id === user?.id ? "owner" : "tenant");
  const otherName = scope === "booking"
    ? (myRole === "hotel" ? (thread.guest_name || "Guest") : (thread.hotel_name || "Hotel"))
    : (myRole === "owner" ? thread.tenant_name : "Owner");
  const backTo = scope === "booking" ? "/bookings" : "/my-rentals";
  const refLabel = scope === "booking" ? "Booking" : "Rental";

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-8 py-8 flex flex-col" style={{ minHeight: "calc(100vh - 100px)" }} data-testid="chat-page">
      <Link to={backTo} className="text-xs text-neutral-500 hover:text-midnight inline-flex items-center gap-1 mb-3">
        <ArrowLeft size={14} /> Back
      </Link>

      <div className="flex items-center gap-4 pb-4 border-b border-black/5">
        <div className="w-12 h-12 rounded-full bg-midnight text-bone flex items-center justify-center font-serif text-xl">
          {(otherName || "?").charAt(0).toUpperCase()}
        </div>
        <div>
          <h1 className="font-serif text-xl text-midnight" data-testid="chat-title">{otherName}</h1>
          <p className="text-xs text-neutral-500">{refLabel} #{String(targetId).slice(0, 8)}</p>
        </div>
      </div>

      <div className="my-3 px-4 py-2 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-center gap-2" data-testid="chat-notice">
        <ShieldWarning size={16} weight="fill" /> For your safety, phone numbers and emails are hidden inside the app.
      </div>

      <div ref={listRef} className="flex-1 space-y-2 overflow-y-auto py-4" data-testid="chat-messages">
        {thread.messages.length === 0 && (
          <p className="text-center text-sm text-neutral-500 py-16">Start the conversation</p>
        )}
        {thread.messages.map((m) => {
          const mine = m.sender_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${
                mine ? "bg-midnight text-bone rounded-br-sm" : "bg-stone text-midnight rounded-bl-sm"
              }`} data-testid={`msg-${m.id}`}>
                <p className="whitespace-pre-wrap break-words">{m.body}</p>
                <p className={`text-[10px] mt-1 ${mine ? "text-bone/60" : "text-neutral-500"}`}>
                  {new Date(m.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                  {mine && m.read_at && " · Read"}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <form onSubmit={send} className="pt-3 border-t border-black/5 flex gap-2" data-testid="chat-composer">
        <input
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Type a message…"
          className="flex-1 px-4 py-3 rounded-full border border-black/10 focus:outline-none focus:border-gold text-sm"
          data-testid="chat-input"
          maxLength={2000}
        />
        <button type="submit" disabled={sending || !body.trim()}
          className="w-12 h-12 rounded-full bg-midnight hover:bg-midnight-hover text-bone flex items-center justify-center disabled:opacity-50"
          data-testid="chat-send-btn">
          <PaperPlaneTilt size={18} weight="fill" />
        </button>
      </form>
    </div>
  );
}
